import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Tag, Globe, Mail, Calendar, Loader2, Cpu, MemoryStick, HardDrive, Wifi, ShieldCheck, BarChart3 } from 'lucide-react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProviderAvatar({ name, logo_url }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  if (logo_url && !imgErr) {
    return (
      <img src={logo_url} alt={`${name} Logo`} onError={() => setImgErr(true)}
        className="w-12 h-12 object-contain bg-white/5 border border-slate-700 rounded-xl flex-shrink-0" />
    );
  }
  return (
    <div className="w-12 h-12 flex items-center justify-center font-bold text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex-shrink-0 text-base">
      {initials}
    </div>
  );
}

export default function ProductDetail() {
  const { id, pid } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [product, setProduct] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [provRes, prodsRes] = await Promise.all([
          axios.get(`${API}/hostings/${id}`),
          axios.get(`${API}/hostings/${id}/products`),
        ]);
        setProvider(provRes.data);
        const found = prodsRes.data.find(p => String(p.id) === String(pid));
        if (!found) throw new Error('Produkt nicht gefunden');
        setProduct(found);
      } catch {
        setError(t.product.notFound);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, pid]);

  return (
    <div className="min-h-screen bg-[#020817] flex flex-col">
      <Navbar />
      <main className="flex-1 py-12 px-4 md:px-8 max-w-7xl mx-auto w-full">

        {/* Back */}
        <button onClick={() => navigate(`/anbieter/${id}`)} data-testid="back-to-provider"
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-400 transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> {t.product.backToProvider}
        </button>

        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : error ? (
          <div className="text-center py-32 text-slate-400">{error}</div>
        ) : product && provider ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* Main product info */}
            <div className="lg:col-span-3 space-y-5">
              <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-7">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-800 text-slate-400 border border-slate-700 mb-3">
                      <Tag className="w-2.5 h-2.5" />
                      {t.categories[product.category] || product.category}
                    </span>
                    <h1 className="text-2xl font-bold text-white" data-testid="product-detail-name">{product.name}</h1>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-3xl font-bold text-cyan-400" data-testid="product-detail-price">
                      {product.price > 0 ? `${Number(product.price).toFixed(2)} €` : t.product.free}
                    </p>
                    {product.price > 0 && (
                      <p className="text-sm text-slate-500 mt-0.5">{t.product.period[product.price_period] || ''}</p>
                    )}
                  </div>
                </div>

                {product.description ? (
                  <p className="text-slate-300 leading-relaxed text-sm" data-testid="product-detail-description">
                    {product.description}
                  </p>
                ) : (
                  <p className="text-slate-600 text-sm italic">{t.product.noDescription}</p>
                )}

                {/* Technical Specs */}
                {(() => {
                  const SPEC_FIELDS = [
                    { key:'cpu_type',        label: t.specs.cpu_type,        icon: Cpu },
                    { key:'cpu_cores',       label: t.specs.cpu_cores,       icon: Cpu },
                    { key:'ram_type',        label: t.specs.ram_type,        icon: MemoryStick },
                    { key:'ram_size',        label: t.specs.ram_size,        icon: MemoryStick },
                    { key:'storage_type',    label: t.specs.storage_type,    icon: HardDrive },
                    { key:'storage_size',    label: t.specs.storage_size,    icon: HardDrive },
                    { key:'bandwidth',       label: t.specs.bandwidth,       icon: Wifi },
                    { key:'ddos_protection', label: t.specs.ddos_protection, icon: ShieldCheck },
                    { key:'traffic',         label: t.specs.traffic,         icon: BarChart3 },
                  ];
                  const filled = SPEC_FIELDS.filter(f => product[f.key]);
                  if (!filled.length) return null;
                  return (
                    <div className="mt-5 pt-5 border-t border-slate-800" data-testid="product-specs">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t.specs.title}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {filled.map(({ key, label, icon: Icon }) => (
                          <div key={key} className="flex items-center gap-3 px-3 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                            <Icon className="w-4 h-4 text-cyan-500/70 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-slate-500">{label}</p>
                              <p className="text-sm font-medium text-slate-200 truncate">{product[key]}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {product.url && (
                  <div className="mt-6 pt-5 border-t border-slate-800">
                    <a href={product.url} target="_blank" rel="noopener noreferrer" data-testid="product-detail-link"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-xl hover:bg-cyan-400 transition-colors text-sm">
                      {t.product.toProduct} <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Provider sidebar */}
            <div>
              <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-5" data-testid="product-provider-card">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">{t.product.sidebarTitle}</p>
                <div className="flex items-center gap-3 mb-4">
                  <ProviderAvatar name={provider.name} logo_url={provider.logo_url} />
                  <div>
                    <p className="font-semibold text-white text-sm">{provider.name}</p>
                    {provider.tagline && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{provider.tagline}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {provider.founded_year && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="w-3.5 h-3.5 text-slate-600" />
                      {t.product.since} {provider.founded_year}
                    </div>
                  )}
                  {provider.url && (
                    <a href={provider.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
                      <Globe className="w-3.5 h-3.5 text-slate-600" />
                      {provider.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  )}
                  {provider.contact_email && (
                    <a href={`mailto:${provider.contact_email}`}
                      className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors">
                      <Mail className="w-3.5 h-3.5 text-slate-600" />
                      {provider.contact_email}
                    </a>
                  )}
                </div>

                <button onClick={() => navigate(`/anbieter/${id}`)} data-testid="view-all-products-btn"
                  className="mt-5 w-full py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors">
                  {t.product.allProducts}
                </button>

                {provider.url && (
                  <a href={provider.url} target="_blank" rel="noopener noreferrer"
                    className="mt-2 flex items-center justify-center gap-2 w-full py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/20 transition-colors">
                    {t.product.visitWebsite} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>

          </div>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
